import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {AccountClaims} from '../types';
import useClientConfig from '../lib/useClientConfig';
import {useIsAuthenticated, useMsal} from '@azure/msal-react';
import {useTeamsContext} from './TeamsContext';
import {authentication} from '@microsoft/teams-js';
import {jwtDecode} from 'jwt-decode';
import getAccessTokenImpl, {
    acquireMsalTokenSilent,
    SILENT_TOKEN_TIMEOUT_MS
} from '../lib/getAccessToken';
import withTimeout from '../lib/withTimeout';
import {UserProfileUpdater} from './UserProfileUpdater';
import {PendingApprovalsUpdater} from './PendingApprovalsUpdater';
import {PendingCertificationsUpdater} from './PendingCertificationsUpdater';
import {ClientUserProfile} from '../api/user/useGetUserProfile';
import Authorization from '../lib/Authorization';
import {Spinner} from 'reactstrap';
import SessionExpiredBanner from '../components/ui/SessionExpiredBanner';
import RoleNames from '../lib/RoleNames';
import {
    clearLegacyRefreshToken,
    clearLocalTokens,
    clearSessionHint,
    getLegacyRefreshToken,
    getLocalAccessToken,
    getLocalTokenExpiryMs,
    hasSessionHint,
    hasValidLocalToken,
    localTokenExpiringSoon,
    localTokenHasExpired,
    setSessionHint,
    storeLocalAccessToken
} from '../lib/localAuth';
import {
    refreshLocalTokenWithRetry,
    RefreshOutcome
} from '../api/auth/refreshLocalToken';
import {LocalAuthTokens} from '../api/auth/localAuthTypes';
import baseUrl from '../api/baseUrl';
import {queryClient} from '../lib/queryClient';
import {AuthUnavailableError, SessionExpiredError} from '../api/authErrors';

// Refresh the local JWT this long before it actually expires, so an active user
// never makes a request with a stale token.
const REFRESH_AHEAD_MS = 120_000;
// Collapse near-simultaneous wake events (focus + visibilitychange often fire together).
const WAKE_THROTTLE_MS = 500;

type RefreshStatus = RefreshOutcome['status'];

type AcquireResult =
    | {token: string; reason: 'ok'}
    | {token: null; reason: 'transient' | 'expired' | 'unavailable'};

/** Diagnostic snapshot of the most recent sign-in step that failed, surfaced in
 *  the recovery banner as copyable "send this to support" details. */
export interface AuthFailureInfo {
    step:
        | 'microsoft-sign-in'
        | 'teams-sso'
        | 'token-exchange'
        | 'session-refresh';
    detail: string;
    status?: number;
    email?: string;
    oid?: string;
    tenantId?: string;
    at: string;
}

export interface AuthState {
    isAuthenticated: boolean;
    getAccessToken: () => Promise<string | null>;
    getAccessTokenOrThrow: () => Promise<string>;
    getTeamsConsent: () => Promise<void>;
    accountClaims: AccountClaims | null;
    isAdmin: boolean;
    currentUserEmail: string;
    isThetaAdmin: boolean;
    pendingApprovalsCount: number;
    pendingCertificationsCount: number;
    userProfile: ClientUserProfile | null;
    groups: string[];
    roles: string[];
    isInRole: (role: string) => boolean;
    isAuthorizedForResource: (resource: Authorization) => boolean;
    onLocalAuthenticated: (tokens: LocalAuthTokens) => void;
    sessionExpired: boolean;
    lastAuthFailure: AuthFailureInfo | null;
    recoverSession: () => Promise<void>;
    logout: () => void;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({
    children
}) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accountClaims, setAccountClaims] = useState<AccountClaims | null>(
        null
    );
    const [userProfile, setUserProfile] = useState<ClientUserProfile | null>(
        null
    );
    const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
    const [pendingCertificationsCount, setPendingCertificationsCount] =
        useState(0);

    // Local JWT state — initialised from sessionStorage on mount
    const [localAccessToken, setLocalAccessToken] = useState<string | null>(
        () => getLocalAccessToken()
    );
    const localIsAuthenticated = localAccessToken
        ? !localTokenHasExpired(localAccessToken)
        : false;

    // True only when a session that previously worked can no longer be silently
    // restored — surfaces a single "session expired" banner instead of scattered
    // "Unauthorized." panels.
    const [sessionExpired, setSessionExpired] = useState(false);

    // True while a fresh browsing context (new window, reopened extension side
    // panel) is silently restoring its session from the HttpOnly refresh cookie.
    // Children are held back behind a spinner so the login page never flashes.
    const [isRestoringSession, setIsRestoringSession] = useState(
        () => hasSessionHint() && !hasValidLocalToken()
    );

    // Diagnostic details of the most recent failed sign-in step. Non-null makes
    // the recovery banner show "unable to log you in" + copyable support info,
    // so a failed exchange never strands the user without a way forward.
    // Cleared whenever a token is successfully obtained.
    const [lastAuthFailure, setLastAuthFailure] =
        useState<AuthFailureInfo | null>(null);

    const refreshingRef = useRef<Promise<RefreshStatus> | null>(null);
    const recoveringRef = useRef<Promise<string | null> | null>(null);
    const refreshTimerRef = useRef<number | null>(null);
    const lastWakeRef = useRef(0);

    const isAdmin = useMemo(
        () => userProfile?.effectiveRoles.includes(RoleNames.SysAdmin) ?? false,
        [userProfile]
    );

    const groups = useMemo(
        () => userProfile?.groupMemberships.map(g => g.groupName) ?? [],
        [userProfile]
    );

    const roles = useMemo(
        () => userProfile?.effectiveRoles ?? [],
        [userProfile]
    );

    const currentUserEmail = useMemo(
        () => accountClaims?.preferred_username ?? '',
        [accountClaims]
    );

    const isThetaAdmin = useMemo(
        () => isAdmin && currentUserEmail.endsWith('@theta.co.nz'),
        [isAdmin, currentUserEmail]
    );

    const {clientId, tenantId} = useClientConfig();
    const {instance, accounts} = useMsal();
    const {isRunningInTeams} = useTeamsContext();
    const isMsalAuthenticated = useIsAuthenticated();

    // Record a failed sign-in step, enriched with whatever identity info the
    // client already knows (from the MSAL account) so support can locate the user.
    const recordAuthFailure = useCallback(
        (failure: Pick<AuthFailureInfo, 'step' | 'detail' | 'status'>) => {
            const account = accounts[0];
            setLastAuthFailure({
                ...failure,
                email:
                    account?.idTokenClaims?.preferred_username ??
                    account?.username,
                oid: account?.idTokenClaims?.oid,
                tenantId,
                at: new Date().toISOString()
            });
        },
        [accounts, tenantId]
    );

    // ── onLocalAuthenticated must be defined first — used by effects below ──
    // Only the short-lived access token is stored; the refresh token travels in
    // an HttpOnly cookie the server set on the login/refresh response.
    const onLocalAuthenticated = useCallback((tokens: LocalAuthTokens) => {
        storeLocalAccessToken(tokens.access_token);
        setSessionHint();
        setLocalAccessToken(tokens.access_token);
        setIsAuthenticated(true);
        setSessionExpired(false);
        setLastAuthFailure(null);
    }, []);

    // Exchanges an MSAL/Teams access token for a local JWT. Shared by the
    // auto-exchange effect, Teams consent, and silent recovery.
    const exchangeMsalForLocal = useCallback(
        async (msalToken: string): Promise<LocalAuthTokens | null> => {
            try {
                const response = await fetch(
                    `${baseUrl}/api/auth/msal-exchange`,
                    {
                        method: 'POST',
                        // include: the response sets the HttpOnly refresh cookie,
                        // which the browser drops on cross-origin (dev) requests
                        // without credentials.
                        credentials: 'include',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({msalToken})
                    }
                );

                if (!response.ok) {
                    const bodyText = await response.text();
                    console.error('MSAL token exchange failed:', bodyText);

                    // The server replies with ProblemDetails — surface its
                    // reason in the support diagnostics rather than discarding it.
                    let serverReason = bodyText;
                    try {
                        const problem = JSON.parse(bodyText) as {
                            title?: string;
                            errors?: {message?: string}[];
                        };
                        serverReason =
                            problem.errors
                                ?.map(e => e.message)
                                .filter(Boolean)
                                .join('; ') ||
                            problem.title ||
                            bodyText;
                    } catch {
                        // Not JSON — keep the raw text.
                    }

                    recordAuthFailure({
                        step: 'token-exchange',
                        status: response.status,
                        detail:
                            serverReason ||
                            'The server rejected the sign-in exchange.'
                    });
                    return null;
                }

                const tokens: LocalAuthTokens = await response.json();
                onLocalAuthenticated(tokens);
                return tokens;
            } catch (err) {
                console.error('MSAL token exchange error:', err);
                recordAuthFailure({
                    step: 'token-exchange',
                    detail:
                        err instanceof Error
                            ? `Could not reach the sign-in service: ${err.message}`
                            : 'Could not reach the sign-in service.'
                });
                return null;
            }
        },
        [onLocalAuthenticated, recordAuthFailure]
    );

    // Non-Teams: isAuthenticated = local JWT valid OR MSAL authenticated (MSAL will trigger exchange)
    useEffect(() => {
        if (isRunningInTeams === false) {
            setIsAuthenticated(localIsAuthenticated || isMsalAuthenticated);
        }
    }, [localIsAuthenticated, isMsalAuthenticated, isRunningInTeams]);

    // Teams: isAuthenticated = local JWT valid only
    useEffect(() => {
        if (isRunningInTeams === true) {
            setIsAuthenticated(localIsAuthenticated);
        }
    }, [localIsAuthenticated, isRunningInTeams]);

    const getAccount = useCallback(async () => {
        if (isRunningInTeams === true) {
            try {
                const token = await authentication.getAuthToken({tenantId});
                const account = jwtDecode(token) as {
                    oid?: string;
                    preferred_username?: string;
                    name?: string;
                };
                setAccountClaims(account);
            } catch (error) {
                console.error(error);
                setAccountClaims(null);
            }
        } else if (localIsAuthenticated && localAccessToken) {
            // For local JWT users, decode claims from local token
            const decoded = jwtDecode<{
                oid?: string;
                upn?: string;
                email?: string;
                given_name?: string;
                family_name?: string;
                name?: string;
            }>(localAccessToken);
            setAccountClaims({
                oid: decoded.oid,
                preferred_username: decoded.upn ?? decoded.email,
                name:
                    [decoded.given_name, decoded.family_name]
                        .filter(Boolean)
                        .join(' ') ||
                    decoded.name ||
                    decoded.upn
            });
        } else if (accounts.length > 0) {
            const account = accounts[0];
            setAccountClaims({
                oid: account.idTokenClaims?.oid,
                preferred_username: account.idTokenClaims?.preferred_username,
                name: account.idTokenClaims?.name
            });
        }
    }, [
        accounts,
        isRunningInTeams,
        tenantId,
        localIsAuthenticated,
        localAccessToken
    ]);

    useEffect(() => {
        if (isAuthenticated) {
            getAccount();
        }
    }, [getAccount, isAuthenticated]);

    // ── Auto-exchange MSAL token for local JWT ────────────────────────────
    // When MSAL authenticates (non-Teams) and we don't yet have a local token,
    // exchange the MSAL access token for a local JWT.
    useEffect(() => {
        if (
            !isMsalAuthenticated ||
            localIsAuthenticated ||
            isRunningInTeams !== false
        ) {
            return;
        }

        const exchange = async () => {
            const msalToken = await getAccessTokenImpl(
                clientId,
                tenantId,
                {instance, accounts},
                false
            );

            if (!msalToken) {
                return;
            }

            await exchangeMsalForLocal(msalToken);
        };

        exchange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMsalAuthenticated]);

    // ── Teams consent ─────────────────────────────────────────────────────
    const getTeamsConsent = useCallback(async () => {
        try {
            const teamsToken = await authentication.getAuthToken({tenantId});
            const tokens = await exchangeMsalForLocal(teamsToken);
            if (!tokens) {
                setIsAuthenticated(false);
            }
        } catch (error) {
            setIsAuthenticated(false);
            console.error(error);
        }
    }, [tenantId, exchangeMsalForLocal]);

    // ── Token management ──────────────────────────────────────────────────

    const logout = useCallback(() => {
        // Best-effort server-side logout: revokes the refresh-token family and
        // deletes the HttpOnly cookie. Fire-and-forget so a network failure
        // never blocks clearing local state.
        void fetch(`${baseUrl}/api/auth/logout`, {
            method: 'POST',
            credentials: 'include',
            headers: {'X-TA-Auth': '1'}
        }).catch(() => undefined);

        clearLocalTokens();
        clearSessionHint();
        setLocalAccessToken(null);
        setUserProfile(null);
        setIsAuthenticated(false);
        setSessionExpired(false);
        setLastAuthFailure(null);
        if (accounts.length > 0) {
            instance.logoutPopup().catch(console.error);
        }
    }, [accounts, instance]);

    // Refresh the local JWT using the HttpOnly refresh cookie. Concurrent callers
    // share one in-flight request via refreshingRef. On success the new access
    // token is persisted (the rotated refresh token rides back in the cookie); on
    // 'transient' nothing is cleared (caller may retry); on 'expired' the session
    // hint is cleared so future boots skip the doomed restore attempt.
    const refreshLocalSession =
        useCallback(async (): Promise<RefreshStatus> => {
            if (refreshingRef.current) {
                return refreshingRef.current;
            }

            const promise = (async (): Promise<RefreshStatus> => {
                // Pre-cookie sessions still hold a refresh token in
                // sessionStorage; presenting it migrates them to a cookied,
                // server-persisted session.
                const legacyRefreshToken = getLegacyRefreshToken();
                const outcome = await refreshLocalTokenWithRetry(
                    legacyRefreshToken ?? undefined
                );
                if (outcome.status === 'success') {
                    storeLocalAccessToken(outcome.tokens.access_token);
                    setSessionHint();
                    clearLegacyRefreshToken();
                    setLocalAccessToken(outcome.tokens.access_token);
                    setSessionExpired(false);
                    setLastAuthFailure(null);
                } else if (outcome.status === 'expired') {
                    clearSessionHint();
                } else {
                    // Already retried with backoff — this is a sustained outage,
                    // not a blip. Surface it so the user isn't silently stuck.
                    recordAuthFailure({
                        step: 'session-refresh',
                        detail: 'Could not reach the sign-in service to refresh your session (network or server error).'
                    });
                }

                return outcome.status;
            })();

            refreshingRef.current = promise;
            try {
                return await promise;
            } finally {
                refreshingRef.current = null;
            }
        }, [recordAuthFailure]);

    // ── Boot-time silent restore ──────────────────────────────────────────
    // A fresh browsing context has an empty sessionStorage (no access token, no
    // MSAL cache) but the refresh cookie survives. One refresh call restores the
    // session without any login UI; on failure the hint is cleared inside
    // refreshLocalSession and the normal MSAL/Teams flows take over.
    useEffect(() => {
        if (!isRestoringSession) {
            return;
        }

        let cancelled = false;
        void (async () => {
            await refreshLocalSession();
            if (!cancelled) {
                setIsRestoringSession(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // Mount-only: the restore must run exactly once per browsing context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Core of silent recovery: acquire an MSAL/Teams token and exchange it for
    // a local JWT. Concurrent callers share one in-flight attempt via
    // recoveringRef (same pattern as refreshLocalSession) — on app load a burst
    // of requests would otherwise each run their own msal-exchange, hammering
    // the server and writing duplicate profile audit events.
    const runSilentRecovery = useCallback(async (): Promise<string | null> => {
        if (recoveringRef.current) {
            return recoveringRef.current;
        }

        const promise = (async (): Promise<string | null> => {
            try {
                const msalToken =
                    isRunningInTeams === true
                        ? await withTimeout(
                              authentication.getAuthToken({tenantId}),
                              SILENT_TOKEN_TIMEOUT_MS,
                              'Teams token acquisition'
                          )
                        : await acquireMsalTokenSilent(
                              clientId,
                              {
                                  instance,
                                  accounts
                              },
                              reason =>
                                  recordAuthFailure({
                                      step: 'microsoft-sign-in',
                                      detail: reason
                                  })
                          );

                if (msalToken) {
                    const tokens = await exchangeMsalForLocal(msalToken);
                    if (tokens) {
                        return tokens.access_token;
                    }
                }
            } catch (err) {
                console.error('Silent session recovery failed', err);
                recordAuthFailure({
                    step:
                        isRunningInTeams === true
                            ? 'teams-sso'
                            : 'microsoft-sign-in',
                    detail: err instanceof Error ? err.message : String(err)
                });
            }

            return null;
        })();

        recoveringRef.current = promise;
        try {
            return await promise;
        } finally {
            recoveringRef.current = null;
        }
    }, [
        isRunningInTeams,
        tenantId,
        clientId,
        instance,
        accounts,
        exchangeMsalForLocal,
        recordAuthFailure
    ]);

    // Attempt to silently mint a new local JWT from the (usually still-valid)
    // Azure AD SSO session — no popup. Returns the new access token, or null.
    // When it fails and surfaceBannerOnFailure is set, the session is marked
    // expired so the banner prompts the user to sign in again. The failure
    // side-effect stays per-caller so joining a shared in-flight attempt never
    // applies another caller's banner preference.
    const attemptSilentRecovery = useCallback(
        async ({
            surfaceBannerOnFailure = true
        }: {surfaceBannerOnFailure?: boolean} = {}): Promise<string | null> => {
            const accessToken = await runSilentRecovery();
            if (accessToken) {
                return accessToken;
            }

            if (surfaceBannerOnFailure) {
                clearLocalTokens();
                setLocalAccessToken(null);
                setSessionExpired(true);
            }

            return null;
        },
        [runSilentRecovery]
    );

    // Acquires a usable access token, reporting WHY it failed so callers can
    // decide whether to retry (transient/unavailable) or surface the expired
    // banner (expired). The reason travels with the result rather than via React
    // state, which would be stale within the same tick.
    const acquireToken = useCallback(async (): Promise<AcquireResult> => {
        // 1. Valid local JWT — fast path.
        const localToken = getLocalAccessToken();
        if (localToken && !localTokenHasExpired(localToken)) {
            return {token: localToken, reason: 'ok'};
        }

        // 2. We (probably) have a session to refresh — the refresh token itself
        //    is an HttpOnly cookie the client can't inspect, so the hint (or a
        //    legacy sessionStorage token) stands in for "a session exists".
        if (hasSessionHint() || getLegacyRefreshToken()) {
            const outcome = await refreshLocalSession();
            if (outcome === 'success') {
                const refreshed = getLocalAccessToken();
                return refreshed
                    ? {token: refreshed, reason: 'ok'}
                    : {token: null, reason: 'transient'};
            }
            if (outcome === 'transient') {
                // Keep tokens; the caller should retry. Don't flag the session dead.
                return {token: null, reason: 'transient'};
            }
            // 'expired' — refresh token is dead; try to self-heal via SSO.
            const recovered = await attemptSilentRecovery({
                surfaceBannerOnFailure: true
            });
            return recovered
                ? {token: recovered, reason: 'ok'}
                : {token: null, reason: 'expired'};
        }

        // 3. No local session yet (e.g. MSAL authenticated but not exchanged).
        //    Mint silently; don't show the expired banner if it can't.
        const recovered = await attemptSilentRecovery({
            surfaceBannerOnFailure: false
        });
        return recovered
            ? {token: recovered, reason: 'ok'}
            : {token: null, reason: 'unavailable'};
    }, [refreshLocalSession, attemptSilentRecovery]);

    // Returns the token or null. Used by SignalR factories and image loaders
    // that tolerate a missing token (they fail/reconnect on their own).
    const getAccessToken = useCallback(
        async (): Promise<string | null> => (await acquireToken()).token,
        [acquireToken]
    );

    // Returns the token or THROWS — never sends an unauthenticated request. Used
    // by the authenticated fetch layer so a missing token becomes a typed error
    // (retryable AuthUnavailableError, or suppressed SessionExpiredError) instead
    // of a server 401 that renders a scattered "Unauthorized." panel.
    const getAccessTokenOrThrow = useCallback(async (): Promise<string> => {
        const result = await acquireToken();
        if (result.reason === 'ok') {
            return result.token;
        }
        if (result.reason === 'expired') {
            throw new SessionExpiredError();
        }
        throw new AuthUnavailableError();
    }, [acquireToken]);

    // Refresh the local session and, if the refresh token is dead, self-heal via
    // silent SSO. Shared by the proactive timer and the wake-event handler so
    // both recover identically.
    const refreshOrRecover = useCallback(async (): Promise<void> => {
        const outcome = await refreshLocalSession();
        if (outcome === 'expired') {
            await attemptSilentRecovery({surfaceBannerOnFailure: true});
        }
    }, [refreshLocalSession, attemptSilentRecovery]);

    // User-initiated recovery (the banner button). A real gesture, so an
    // interactive popup is permitted as a last resort.
    const recoverSession = useCallback(async (): Promise<void> => {
        let recovered =
            (await attemptSilentRecovery({surfaceBannerOnFailure: false})) !==
            null;

        if (!recovered) {
            if (isRunningInTeams === true) {
                await getTeamsConsent();
                recovered = hasValidLocalToken();
                if (!recovered) {
                    recordAuthFailure({
                        step: 'teams-sso',
                        detail: 'Interactive Teams sign-in did not complete.'
                    });
                }
            } else {
                const msalToken = await getAccessTokenImpl(
                    clientId,
                    tenantId,
                    {instance, accounts},
                    false
                );
                if (msalToken) {
                    recovered =
                        (await exchangeMsalForLocal(msalToken)) !== null;
                } else {
                    // Silent AND popup both failed — record it so the banner's
                    // support details reflect this attempt, not just the last
                    // background one.
                    recordAuthFailure({
                        step: 'microsoft-sign-in',
                        detail: 'Interactive Microsoft sign-in did not complete (popup closed, blocked, or sign-in failed).'
                    });
                }
            }
        }

        if (recovered) {
            setSessionExpired(false);
            await queryClient.invalidateQueries();
        }
    }, [
        attemptSilentRecovery,
        isRunningInTeams,
        getTeamsConsent,
        clientId,
        tenantId,
        instance,
        accounts,
        exchangeMsalForLocal,
        recordAuthFailure
    ]);

    // ── Proactive refresh: refresh shortly before the token expires ───────
    useEffect(() => {
        if (refreshTimerRef.current !== null) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }

        if (!isAuthenticated || !localAccessToken) {
            return;
        }

        const expiryMs = getLocalTokenExpiryMs(localAccessToken);
        if (expiryMs === null) {
            return;
        }

        const fireInMs = Math.max(0, expiryMs - Date.now() - REFRESH_AHEAD_MS);
        refreshTimerRef.current = window.setTimeout(() => {
            void refreshOrRecover();
        }, fireInMs);

        return () => {
            if (refreshTimerRef.current !== null) {
                window.clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [isAuthenticated, localAccessToken, refreshOrRecover]);

    // ── Wake events: refresh when the user returns to an idle tab ─────────
    useEffect(() => {
        const handleWake = () => {
            // Only act if there is an existing session worth keeping alive.
            const token = getLocalAccessToken();
            if (!hasSessionHint() && !token) {
                return;
            }

            const now = Date.now();
            if (now - lastWakeRef.current < WAKE_THROTTLE_MS) {
                return;
            }
            lastWakeRef.current = now;

            if (token && !localTokenExpiringSoon(token)) {
                return;
            }

            void refreshOrRecover();
        };

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                handleWake();
            }
        };

        window.addEventListener('focus', handleWake);
        window.addEventListener('online', handleWake);
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            window.removeEventListener('focus', handleWake);
            window.removeEventListener('online', handleWake);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [refreshOrRecover]);

    const isInRole = useCallback(
        (role: string): boolean => roles.includes(role),
        [roles]
    );

    const isAuthorizedForResource = useCallback(
        (resource: Authorization): boolean => {
            if (!resource.authorize) {
                return true;
            }

            if (!isAuthenticated) {
                return false;
            }

            if (resource.roles && resource.roles.length > 0) {
                return resource.roles.some(r => isInRole(r));
            }

            return true;
        },
        [isAuthenticated, isInRole]
    );

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                getAccessToken,
                getAccessTokenOrThrow,
                getTeamsConsent,
                accountClaims,
                isAdmin,
                currentUserEmail,
                isThetaAdmin,
                pendingApprovalsCount,
                pendingCertificationsCount,
                userProfile,
                groups,
                roles,
                isInRole,
                isAuthorizedForResource,
                onLocalAuthenticated,
                sessionExpired,
                lastAuthFailure,
                recoverSession,
                logout
            }}
        >
            <UserProfileUpdater onUpdate={setUserProfile} />
            <PendingApprovalsUpdater
                isAuthenticated={isAuthenticated}
                isAdmin={isAdmin}
                onUpdate={setPendingApprovalsCount}
            />
            <PendingCertificationsUpdater
                isAuthenticated={isAuthenticated}
                isAdmin={isAdmin}
                onUpdate={setPendingCertificationsCount}
            />
            {/* Hold children back while the boot-time cookie restore is in
                flight so a restorable session never flashes the login page.
                Otherwise only show a spinner if authenticated but profile not
                yet loaded. Render the session-expired banner there too: if the
                profile request is what discovered a dead session, recovery may
                have failed while isAuthenticated is still true (MSAL) and
                userProfile is null — the banner must stay reachable instead of
                being hidden behind the spinner. */}
            {isRestoringSession ? (
                <Spinner color="primary" />
            ) : !isAuthenticated || userProfile ? (
                <>{children}</>
            ) : (
                <>
                    <SessionExpiredBanner />
                    <Spinner color="primary" />
                </>
            )}
        </AuthContext.Provider>
    );
};

export function useAuthContext(): AuthState {
    const authState = useContext(AuthContext);
    if (!authState) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }

    return authState;
}
